import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactECharts from 'echarts-for-react';
import { GET_DATAGUARD_BEHAVIOR_DATA } from '../Utility/DataguardConstant';
import Page_Loader from '../../../assets/vizr_images/page_loader.gif';
import { calculatePercentage } from "../../SysGuard/sysGuardConstants";

const DataguardBehaviorModel = ({loc}) => {
  const dispatch = useDispatch();
  const chartRef = useRef(null);
  const behaviorData = useSelector((state) => state.DataguardReducer.behaviorData);
  const behaviorLoader = useSelector((state) => state.DataguardReducer.behaviorLoader);
  const fromTimeStamp = useSelector((state) => state.DataguardReducer.fromTimeStamp);
  const toTimeStamp = useSelector((state) => state.DataguardReducer.toTimeStamp);

  useEffect(() => {
    if (fromTimeStamp && toTimeStamp) {
      dispatch({
        type: GET_DATAGUARD_BEHAVIOR_DATA,
        payload: {
          fromTimeStamp,
          toTimeStamp
        }
      });
    }
  }, [dispatch, fromTimeStamp, toTimeStamp]);

  const { series, indicator, legend } = useMemo(() => {
    if (!behaviorData || !behaviorData.countByPolicy) return { series: [], indicator: [], legend: [] };
    const series = [
      {
        name: 'Policy Distribution',
        type: 'radar',
        data: [
          {
            value: [],
            name: 'Alerts',
            areaStyle: {
              color: '#e71414'
            },
            lineStyle: {
              color: '#e71414',
              width: 2
            },
            itemStyle: {
              color: '#e71414'
            }
          },
        ]
      },
    ];
    const legend = [], indicator = [];

    if (behaviorData.countByPolicy && Array.isArray(behaviorData.countByPolicy)) {
      // Helper function to calculate log scale value
      const getLogValue = (value) => {
        // Add 1 to handle zero values, then take log
        return value > 0 ? Math.log10(value + 1) : 0.2;
      };

      // First pass: collect original values and calculate log values
      let maxLogValue = 2; // Minimum max to avoid zero max issues
      const originalValues = [];
      const logValues = [];

      behaviorData.countByPolicy.forEach(item => {
        const alertCount = item.totalCount || 0;
        const logValue = getLogValue(alertCount);
        
        originalValues.push(alertCount);
        logValues.push(logValue);
        legend.push(item.policyName);
        
        if (logValue > maxLogValue) {
          maxLogValue = logValue;
        }
      });

      // Second pass: push log values for plotting but store original values for tooltips
      behaviorData.countByPolicy.forEach((item, index) => {
        // Push log-scaled value for plotting
        series[0].data[0].value.push(logValues[index]);
        
        // Store indicator with original alert count for tooltips
        indicator.push({
          name: item.policyName,
          alertCount: originalValues[index], // Original value for tooltips
          percentage: behaviorData.totalNoOfAlerts > 0 ? calculatePercentage(originalValues[index], behaviorData?.totalNoOfAlerts) : 0,
          max: maxLogValue > 0 ? maxLogValue : 1 // Use log max for scaling
        });
      });
    }
    return { series, indicator, legend };
  }, [behaviorData]);

  const getOption = () => {
    return {
      tooltip: {
        trigger: 'item',
        triggerOn: 'mousemove|click',
        formatter: function (params) {
          if (params.componentType === 'series') {
            const { name, value } = params;
            let noAlert = true;
            let tooltipContent = `<strong>${name}</strong><br/>`;
            value.forEach((val, index) => {
              if (indicator[index] && indicator[index].alertCount > 0) {
                noAlert = false;
                // Display original alert count, not the log-scaled value
                tooltipContent += `${indicator[index].name}: ${indicator[index].alertCount}<br/>`;
              }
            });
            if (noAlert) {
              tooltipContent += 'No Alerts';
            }
            return tooltipContent;
          }
          return params.name;
        },
        axisPointer: {
          type: 'cross'
        }
      },
      legend: {
        data: legend,
        tooltip: {
          show: true,
          formatter: function (params) {
            return `Policy: ${params.name}`;
          }
        }
      },
      radar: {
        shape: 'polygon',
        indicator: indicator,
        splitNumber: 5,
        radius: loc == 'dashboardPopup' ? '65%' : '70%', // Reduce radius to create space between radar and labels
        center: ['50%', '50%'], // Keep centered
        name: {
          textStyle: {
            fontSize: '10px', // Use fixed pixel size for better control
            color: '#333',
            width: 50, // Limit label width to enable wrapping
            overflow: 'break', // Enable text wrapping
            lineHeight: 10, // Set line height for wrapped text
          },
          formatter: function (value) {
            // Find the matching indicator to get additional data
            const matchedPolicy = indicator.find(ind => ind.name === value);
            const alertCount = matchedPolicy?.alertCount || 0;
            const percentage = matchedPolicy?.percentage || 0;
            
            // Split long text into multiple lines while preserving whole words
            if (value.length > 5) {
              const words = value.split(' ');
              let result = '';
              let currentLine = '';

              words.forEach(word => {
                if ((currentLine + word).length <= 5) {
                  currentLine += (currentLine ? ' ' : '') + word;
                } else {
                  if (currentLine) {
                    result += (result ? '\n' : '') + currentLine;
                    currentLine = word;
                  } else {
                    // Handle case where single word is longer than 15 chars
                    result += (result ? '\n' : '') + word;
                  }
                }
              });

              if (currentLine) {
                result += (result ? '\n' : '') + currentLine;
              }

              // Return rich text format with tooltip data
              return `${result} (${percentage}%)`;
            }
            return `${value} (${percentage}%)`;
          },
        },
        triggerEvent: true, // Enable events for tooltips on axis labels
        splitLine: {
          lineStyle: {
            color: '#ddd'
          }
        },
        splitArea: {
          show: true,
          areaStyle: {
            color: ['rgba(255, 255, 255, 0)', 'rgba(0, 0, 0, 0.02)']
          }
        },
        axisLine: {
          lineStyle: {
            color: '#999'
          }
        }
      },
      series: series
    };
  };

  if (behaviorLoader) {
    return (
      <div className='loader-container'>
        <img src={Page_Loader} alt="Loading..." className='loader-img' />
      </div>
    );
  }
  if (!behaviorData || behaviorData.length === 0) return "No data available";
  let inlineStyle = {};
  if(loc == 'dashboardPopup'){
    inlineStyle = { height: '100%', width: '100%' };
  }
  return (
    <div className="heatmap-container">
      {(indicator && indicator.length && series && series.length) ? <ReactECharts
        ref={chartRef}
        option={getOption()}
        className="policy-echart"
        style={inlineStyle}
        notMerge={true}
        opts={{
          renderer: 'canvas'
        }}
      /> : "No data available"}
    </div>
  );
};

export default DataguardBehaviorModel;
